import {
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { App } from '@/services/app-compat';
import { api } from '@/services/api-class';
import { hasValue, currentUserHasPermission, getModuleType } from '@/utils/legacy-utils';
import { AccessAuthorities } from '@/models';


interface CallCardCitationsGridProps {
  callCardId?: number | null;
  title?: string;
  isReadOnly?: boolean;
}

export function CallCardCitationsGrid({
  callCardId = null,
  title = 'Citations',
  isReadOnly = false,
}: CallCardCitationsGridProps) {
  const { theme } = useTheme();

  const [citations, setCitations] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [sortField, setSortField] = useState('dateOfIssuance');
  const [sortDir, setSortDir] = useState('desc');

  const pageSize = 25;
  const containerRef = useRef<HTMLDivElement>(null);
  const callCardIdRef = useRef(callCardId);

  useEffect(() => { callCardIdRef.current = callCardId; }, [callCardId]);

  const canCreateCitation = useMemo(
    () => currentUserHasPermission(AccessAuthorities.ROLE_CITATION_CREATE),
    []
  );

  const totalPages = useMemo(() => Math.ceil(totalRecords / pageSize) || 1, [totalRecords]);
  const showPagination = useMemo(() => totalPages > 1, [totalPages]);
  const paginationInfo = useMemo(() => {
    const start = (currentPage - 1) * pageSize + 1;
    const end = Math.min(currentPage * pageSize, totalRecords);
    return `${start}-${end} of ${totalRecords}`;
  }, [currentPage, totalRecords]);

  // Mobile check (debounced to prevent re-render thrashing)
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    const checkIfMobile = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => setIsMobile(window.innerWidth <= 1024), 150);
    };
    setIsMobile(window.innerWidth <= 1024);
    window.addEventListener('resize', checkIfMobile);
    return () => {
      window.removeEventListener('resize', checkIfMobile);
      if (timer) clearTimeout(timer);
    };
  }, []);

  const loadCitations = useCallback((page: number = 1, sf: string = sortField, sd: string = sortDir) => {
    if (!callCardIdRef.current) {
      setCitations([]);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    const start = (page - 1) * pageSize;
    const filters = { sortField: sf, sortDir: sd };

    try {
      api.getCitationsForCallCard(
        callCardIdRef.current,
        filters,
        start,
        pageSize,
        (result: any) => {
          setCitations(result.rows || []);
          setTotalRecords(result.records || 0);
          if (hasValue(App.currentCC) && App.currentCC.id === callCardIdRef.current) {
            App.currentCC.citationCount = result.records || 0;
          }
          setIsLoading(false);
        },
        (error: any) => {
          console.error('Citations API error:', error);
          App.showPopupNotification('Failed to load citations.', 'error');
          setIsLoading(false);
        }
      );
    } catch (e) {
      console.error('Citations load exception:', e);
      setIsLoading(false);
    }
  }, [sortField, sortDir]);

  // Load on mount if callCardId is present
  useEffect(() => {
    if (hasValue(callCardId)) {
      loadCitations(1);
    }
  }, []);

  // Reload when callCardId changes
  useEffect(() => {
    if (hasValue(callCardId)) {
      setCurrentPage(1);
      loadCitations(1);
    }
  }, [callCardId]);

  const openNewCitation = useCallback(() => {
    if (isReadOnly) {
      App.showPopupNotification('Edit mode required.', 'warning');
      return;
    }
    if (!canCreateCitation) {
      App.showPopupNotification('You do not have permission to create citations.', 'error');
      return;
    }
    if (callCardId) (window as any).openNewCitationForCallCard(callCardId);
  }, [isReadOnly, canCreateCitation, callCardId]);

  const viewCitationFn = useCallback((citationId: number) => {
    if (callCardId) (window as any).viewCitation(citationId, 'callcard', callCardId);
  }, [callCardId]);

  const getOffenderLink = useCallback((citation: any): string => {
    if (citation.npsdPersonId) {
      const moduleType = getModuleType().toUpperCase();
      return `/?sectionName=${moduleType}&tab=Search&function=Open_Person&multiModule=true&params=${citation.npsdPersonId}`;
    }
    return '';
  }, []);

  const formatDate = useCallback((dateStr: string): string => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return '';
    const month = date.getMonth() + 1;
    const day = date.getDate();
    return (month < 10 ? '0' : '') + month + '/' +
           (day < 10 ? '0' : '') + day + '/' +
           date.getFullYear();
  }, []);

  const formatFine = useCallback((citation: any): string => {
    if (citation.fineToBeDetermined) return 'TBD';
    if (citation.fineAmount !== null && citation.fineAmount !== undefined) {
      return '$' + citation.fineAmount.toFixed(2);
    }
    return 'TBD';
  }, []);

  const formatViolations = useCallback((citation: any): string => {
    if (citation.violationTypeValues && citation.violationTypeValues.length > 0) {
      return citation.violationTypeValues.join(', ');
    }
    return '';
  }, []);

  const getStatusClass = useCallback((status: string): string => {
    if (status === 'issued') return 'status-issued';
    if (status === 'voided') return 'status-voided';
    return 'status-draft';
  }, []);

  const sortBy = useCallback((field: string) => {
    let newDir = 'asc';
    if (sortField === field) {
      newDir = sortDir === 'asc' ? 'desc' : 'asc';
    }
    setSortField(field);
    setSortDir(newDir);
    setCurrentPage(1);
    loadCitations(1, field, newDir);
  }, [sortField, sortDir, loadCitations]);

  const goToPage = useCallback((page: number) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
    loadCitations(page);
  }, [totalPages, loadCitations]);

  const prevPage = useCallback(() => goToPage(currentPage - 1), [goToPage, currentPage]);
  const nextPage = useCallback(() => goToPage(currentPage + 1), [goToPage, currentPage]);

  // Expose refresh method
  useEffect(() => {
    (window as any).CallCardCitationsGrid_refresh = () => loadCitations(currentPage);
    return () => { delete (window as any).CallCardCitationsGrid_refresh; };
  }, [loadCitations, currentPage]);

  return (
    <div className={`citations-grid ${theme}`} ref={containerRef}>
      {/* Header */}
      {/* TODO: Hide "New Citation" button when offenderMode === 2 (Disabled).
          Citation settings (offenderMode) are not loaded in this component.
          Either pass offenderMode as a prop or load citation settings here. */}
      <div className="header">
        <h1>{title}</h1>
        <button
          className="btn-add"
          onClick={openNewCitation}
          disabled={isReadOnly || !canCreateCitation}
          title={isReadOnly ? 'Edit mode required' : (!canCreateCitation ? 'No permission' : 'New Citation')}
        >
          <i className="fas fa-plus"></i> CITATION
        </button>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="loading-state">
          <i className="fas fa-spinner fa-spin"></i> Loading citations...
        </div>
      )}

      {/* Empty State */}
      {!isLoading && citations.length === 0 && (
        <div className="empty-state">
          No citations found for this call card.
        </div>
      )}

      {/* Content wrapper */}
      {!isLoading && citations.length > 0 && (
        <div className="content-wrapper">
          {/* Desktop Table */}
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th className="col-citation-num" onClick={() => sortBy('citationNumber')}>
                    Citation #
                    {sortField === 'citationNumber' && (
                      <i className={sortDir === 'asc' ? 'fas fa-sort-up' : 'fas fa-sort-down'}></i>
                    )}
                  </th>
                  <th className="col-status">Status</th>
                  <th className="col-date" onClick={() => sortBy('dateOfIssuance')}>
                    Date
                    {sortField === 'dateOfIssuance' && (
                      <i className={sortDir === 'asc' ? 'fas fa-sort-up' : 'fas fa-sort-down'}></i>
                    )}
                  </th>
                  <th className="col-offender">Offender</th>
                  <th className="col-violations">Violation(s)</th>
                  <th className="col-fine">Fine</th>
                  <th className="col-actions"></th>
                </tr>
              </thead>
              <tbody>
                {citations.map((citation: any) => (
                  <tr key={'desktop-' + citation.id}>
                    <td>
                      <a role="button" className="citation-link" onClick={() => viewCitationFn(citation.id)}>
                        {citation.citationNumber || 'DRAFT'}
                      </a>
                    </td>
                    <td>
                      <span className={`status-badge ${getStatusClass(citation.status)}`}>
                        {(citation.status || 'draft').toUpperCase()}
                      </span>
                    </td>
                    <td>{formatDate(citation.dateOfIssuance)}</td>
                    <td>
                      {getOffenderLink(citation) ? (
                        <a href={getOffenderLink(citation)}>{citation.offenderName || '\u2014'}</a>
                      ) : (
                        <span>{citation.offenderName || '\u2014'}</span>
                      )}
                    </td>
                    <td>{formatViolations(citation)}</td>
                    <td>{formatFine(citation)}</td>
                    <td className="actions">
                      <button className="btn-view" onClick={() => viewCitationFn(citation.id)}>View</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Pagination */}
            {showPagination && (
              <div className="pagination">
                <span className="pagination-info">{paginationInfo}</span>
                <div className="pagination-controls">
                  <button onClick={prevPage} disabled={currentPage <= 1}>
                    <i className="fas fa-chevron-left"></i>
                  </button>
                  <span className="page-indicator">Page {currentPage} of {totalPages}</span>
                  <button onClick={nextPage} disabled={currentPage >= totalPages}>
                    <i className="fas fa-chevron-right"></i>
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Mobile Cards */}
          <div className="mobile-cards">
            {citations.map((citation: any) => (
              <div key={'mobile-' + citation.id} className="card">
                <div className="card-header">
                  <div className="card-header-content">
                    <a role="button" className="card-citation-num" onClick={() => viewCitationFn(citation.id)}>
                      {citation.citationNumber || 'DRAFT'}
                    </a>
                    <span className={`status-badge ${getStatusClass(citation.status)}`}>
                      {(citation.status || 'draft').toUpperCase()}
                    </span>
                  </div>
                </div>
                <div className="card-grid">
                  <div className="card-field">
                    <div className="card-field-label">Date</div>
                    <div className="card-field-value">{formatDate(citation.dateOfIssuance) || '-'}</div>
                  </div>
                  <div className="card-field">
                    <div className="card-field-label">Fine</div>
                    <div className="card-field-value">{formatFine(citation)}</div>
                  </div>
                  <div className="card-field card-field-full">
                    <div className="card-field-label">Offender</div>
                    <div className="card-field-value">
                      {getOffenderLink(citation) ? (
                        <a href={getOffenderLink(citation)}>{citation.offenderName || '\u2014'}</a>
                      ) : (
                        <span>{citation.offenderName || '\u2014'}</span>
                      )}
                    </div>
                  </div>
                  <div className="card-field card-field-full">
                    <div className="card-field-label">Violation(s)</div>
                    <div className="card-field-value">{formatViolations(citation) || '-'}</div>
                  </div>
                </div>
                <div className="card-actions">
                  <button className="btn-view-mobile" onClick={() => viewCitationFn(citation.id)}>
                    <i className="fas fa-eye"></i> View Details
                  </button>
                </div>
              </div>
            ))}

            {/* Mobile Pagination */}
            {showPagination && (
              <div className="pagination mobile-pagination">
                <span className="pagination-info">{paginationInfo}</span>
                <div className="pagination-controls">
                  <button onClick={prevPage} disabled={currentPage <= 1}>
                    <i className="fas fa-chevron-left"></i>
                  </button>
                  <span className="page-indicator">{currentPage} / {totalPages}</span>
                  <button onClick={nextPage} disabled={currentPage >= totalPages}>
                    <i className="fas fa-chevron-right"></i>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

(window as any).CallCardCitationsGridReact = CallCardCitationsGrid;
